import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "@coworker/locales";
import { ItemNoAndType } from "@coworker/apprestructured/src/shared/types/item";
import { Location } from "@coworker/apprestructured/src/task-management/types/SalesLocation";
import { SalesLocation } from "@coworker/apprestructured/src/task-management/types/SalesLocation";
import { RefillUnitType } from "../constants/RefillUnitType";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { useItemAmountSettings } from "@coworker/apprestructured/src/shared/hooks/item/useItemAmountSettings";
import { useUpdateItemAmountSettings } from "@coworker/apprestructured/src/shared/hooks/item/useItemAmountSettings";
import { useAvailableElevatedStock } from "@coworker/app/src/hooks/useAvailableElevatedStock";
import { RefillQuantityCreatorFragmentConfigProps } from "../components/TaskCreatorAndEditorSheet/creator-fragments/RefillQuantityCreatorFragment/RefillQuantityCreatorFragment";

export const MAXIMUM_QUANTITY = 3000;

type RefillQuantityDetail = {
  type: RefillUnitType;
  quantity: number;
  pieces: number;
};

const sanitizeNumber = (value: number) => {
  return /^[0-9]*$/.test(String(value)) ? Math.max(value, 0) : 0;
};

type UseRefillQuantityProps = {
  configProps: RefillQuantityCreatorFragmentConfigProps;
  task: Record<string, unknown>;
  onTaskChange: (task: Record<string, unknown>) => void;
  itemNoAndType: ItemNoAndType | null;
  refillLocation: Location | null;
  filteredLocations: {
    homeAndPrimaryLocations: SalesLocation[];
    otherSalesLocations: SalesLocation[];
    sgfLocations: SalesLocation[];
  };
};

export const useRefillQuantity = ({
  configProps,
  task,
  onTaskChange,
  itemNoAndType,
  refillLocation,
  filteredLocations,
}: UseRefillQuantityProps) => {
  const { t } = useTranslation();
  const itemNo = itemNoAndType && itemNoAndType.no ? itemNoAndType.no : "";
  const itemType =
    itemNoAndType && itemNoAndType.type ? itemNoAndType.type : "";
  const fullItemId = itemType && itemNo ? `${itemType}${itemNo}` : "";
  const hasSelectedArticle = itemNo.trim().length > 0;
  const hasSelectedRefillLocation =
    !!refillLocation &&
    (refillLocation.slid.trim().length > 0 ||
      refillLocation.custom.trim().length > 0);
  const isRefillQuantityDisabled =
    !hasSelectedArticle || !hasSelectedRefillLocation;

  const requestedQuantityField =
    configProps.requestedQuantityField || "details.requested_quantity";
  const refilledQuantityField =
    configProps.refilledQuantityField || "details.refilled_quantity";
  const pickedQuantityField =
    configProps.pickedQuantityField || "details.picked_quantity";

  const refillLocationData = useMemo(() => {
    if (!refillLocation) return null;
    const refillSlid = refillLocation.slid;
    if (!refillSlid) return null;

    const allLocations = [
      ...filteredLocations.homeAndPrimaryLocations,
      ...filteredLocations.otherSalesLocations,
      ...filteredLocations.sgfLocations,
    ];
    return allLocations.find((loc) => loc.slid === refillSlid);
  }, [refillLocation, filteredLocations]);

  const { data: amountSettings } = useItemAmountSettings(itemNo);
  const updateAmountSettings = useUpdateItemAmountSettings(itemNo);
  const typedAmountSettings = amountSettings as
    | Partial<{
        pieces_per_multi_pack: string | null;
        pieces_per_pallet: string | null;
      }>
    | undefined;

  const piecesPerMultiPack =
    typedAmountSettings?.pieces_per_multi_pack != null &&
    typedAmountSettings.pieces_per_multi_pack !== ""
      ? Number(typedAmountSettings.pieces_per_multi_pack)
      : 1;
  const piecesPerPallet =
    typedAmountSettings?.pieces_per_pallet != null &&
    typedAmountSettings.pieces_per_pallet !== ""
      ? Number(typedAmountSettings.pieces_per_pallet)
      : 1;

  const multiPackSettingValue =
    typedAmountSettings?.pieces_per_multi_pack != null &&
    typedAmountSettings.pieces_per_multi_pack !== ""
      ? Number(typedAmountSettings.pieces_per_multi_pack)
      : null;
  const palletSettingValue =
    typedAmountSettings?.pieces_per_pallet != null &&
    typedAmountSettings.pieces_per_pallet !== ""
      ? Number(typedAmountSettings.pieces_per_pallet)
      : null;

  const hasMultiPackSetting =
    hasSelectedArticle &&
    multiPackSettingValue != null &&
    Number.isFinite(multiPackSettingValue) &&
    multiPackSettingValue > 0;
  const hasPalletSetting =
    hasSelectedArticle &&
    palletSettingValue != null &&
    Number.isFinite(palletSettingValue) &&
    palletSettingValue > 0;

  const taskObject = useMemo(() => task as Record<string, unknown>, [task]);

  const readQuantityDetails = useCallback(
    (field: string): RefillQuantityDetail | null => {
      const value = getByPath(taskObject, field);
      if (value && typeof value === "object") {
        const obj = value as RefillQuantityDetail;
        return {
          type: obj.type ?? null,
          quantity: obj.quantity ?? null,
          pieces: obj.pieces ?? null,
        };
      }
      return null;
    },
    [taskObject]
  );

  const requestedObj = readQuantityDetails(requestedQuantityField);
  const refilledObj = readQuantityDetails(refilledQuantityField);

  const storedQuantity = requestedObj?.quantity ?? refilledObj?.quantity ?? 0;
  const storedType =
    requestedObj?.type ?? refilledObj?.type ?? RefillUnitType.PIECES;

  const { available: availableStock } = useAvailableElevatedStock(fullItemId);

  const actualQuantity =
    requestedObj?.pieces ??
    refilledObj?.pieces ??
    (storedType === RefillUnitType.PIECES
      ? storedQuantity
      : storedType === RefillUnitType.MULTIPACK
        ? storedQuantity * piecesPerMultiPack
        : storedQuantity * piecesPerPallet);

  const availablePieces =
    (actualQuantity ?? typeof availableStock === "number")
      ? availableStock
      : actualQuantity;

  const maxPieces =
    refillLocationData?.assignedSalesSpaceQuantity ??
    (storedQuantity || actualQuantity);

  const convertUnitToPieces = useCallback(
    (amount: number, unit: RefillUnitType) => {
      if (unit === RefillUnitType.MULTIPACK) {
        return amount * piecesPerMultiPack;
      }

      if (unit === RefillUnitType.PALLET) {
        return amount * piecesPerPallet;
      }

      return amount;
    },
    [piecesPerMultiPack, piecesPerPallet]
  );

  const [activeUnit, setActiveUnit] = useState<RefillUnitType>(storedType);

  const getDisplayedUnitValue = useCallback(
    (unit: RefillUnitType) => {
      if (storedType !== unit) {
        return 0;
      }

      return storedQuantity;
    },
    [storedQuantity, storedType]
  );

  const getCurrentPiecesByUnit = useCallback(
    (unit: RefillUnitType) => {
      const amount = getDisplayedUnitValue(unit);
      return convertUnitToPieces(amount, unit);
    },
    [convertUnitToPieces, getDisplayedUnitValue]
  );

  const currentPieces = getCurrentPiecesByUnit(activeUnit);
  const piecesCurrentPieces = getCurrentPiecesByUnit(RefillUnitType.PIECES);
  const multiPackCurrentPieces = getCurrentPiecesByUnit(
    RefillUnitType.MULTIPACK
  );
  const palletCurrentPieces = getCurrentPiecesByUnit(RefillUnitType.PALLET);

  const saveAmountSetting = useCallback(
    async (
      amountSettingKey: "pieces_per_multi_pack" | "pieces_per_pallet",
      value: string
    ) => {
      const nextAmounts = {
        pieces_per_multi_pack:
          typedAmountSettings?.pieces_per_multi_pack ?? null,
        pieces_per_pallet: typedAmountSettings?.pieces_per_pallet ?? null,
      };

      nextAmounts[amountSettingKey] = value;

      await updateAmountSettings.mutateAsync(nextAmounts);
    },
    [
      typedAmountSettings?.pieces_per_multi_pack,
      typedAmountSettings?.pieces_per_pallet,
      updateAmountSettings,
    ]
  );

  const updateRefillQuantity = useCallback(
    (unit: RefillUnitType) => (value: number) => {
      const sanitizedValue = sanitizeNumber(value);
      const nextPieces = convertUnitToPieces(sanitizedValue, unit);

      const quantityObj: RefillQuantityDetail = {
        type: unit,
        quantity: sanitizedValue,
        pieces: nextPieces,
      };
      const emptyQuantityObj: RefillQuantityDetail = {
        type: unit,
        quantity: 0,
        pieces: 0,
      };

      let updatedTask = taskObject;
      updatedTask = setByPath(requestedQuantityField, quantityObj, updatedTask);
      updatedTask = setByPath(
        pickedQuantityField,
        emptyQuantityObj,
        updatedTask
      );
      updatedTask = setByPath(
        refilledQuantityField,
        emptyQuantityObj,
        updatedTask
      );

      onTaskChange(updatedTask);
    },
    [
      requestedQuantityField,
      pickedQuantityField,
      refilledQuantityField,
      convertUnitToPieces,
      onTaskChange,
      taskObject,
    ]
  );

  const handleRefillUnitTypeChange = useCallback(
    (unit: RefillUnitType) => {
      if (unit !== activeUnit) {
        const emptyObj: RefillQuantityDetail = {
          type: unit,
          quantity: 0,
          pieces: 0,
        };

        let updatedTask = taskObject;
        updatedTask = setByPath(requestedQuantityField, emptyObj, updatedTask);
        updatedTask = setByPath(pickedQuantityField, emptyObj, updatedTask);
        updatedTask = setByPath(refilledQuantityField, emptyObj, updatedTask);

        onTaskChange(updatedTask);
      }

      setActiveUnit(unit);
    },
    [
      activeUnit,
      requestedQuantityField,
      pickedQuantityField,
      refilledQuantityField,
      onTaskChange,
      taskObject,
    ]
  );

  const panelConfigs = useMemo(
    () => ({
      [RefillUnitType.PIECES]: {
        label: t("piecesString"),
        panelProps: {
          type: RefillUnitType.PIECES,
          value: getDisplayedUnitValue(RefillUnitType.PIECES),
          onChange: updateRefillQuantity(RefillUnitType.PIECES),
          suffixLabel: t("piecesString"),
          max: maxPieces,
          current: piecesCurrentPieces,
          disabled: isRefillQuantityDisabled,
          isError: piecesCurrentPieces > maxPieces,
          isWarning: piecesCurrentPieces > availablePieces,
          errorMsg: t("exceedingMaxPiecesString"),
          warningMsg: t("exceedingAvailableStockString"),
          isThresholdWarning: piecesCurrentPieces > MAXIMUM_QUANTITY,
        },
      },
      [RefillUnitType.MULTIPACK]: {
        label: t("multipackString"),
        ...(hasMultiPackSetting
          ? {
              panelProps: {
                type: RefillUnitType.MULTIPACK,
                value: getDisplayedUnitValue(RefillUnitType.MULTIPACK),
                onChange: updateRefillQuantity(RefillUnitType.MULTIPACK),
                infoText: t("multiPackUnit_infoText", {
                  defaultValue: `1 ${t("multiPackUnit")} = ${piecesPerMultiPack} ${t("piecesString").toLowerCase()}`,
                }),
                suffixLabel: t("multipacksString"),
                max: maxPieces,
                current: multiPackCurrentPieces,
                disabled: isRefillQuantityDisabled,
                isError: multiPackCurrentPieces > maxPieces,
                isWarning: multiPackCurrentPieces > availablePieces,
                errorMsg: t("exceedingMaxPiecesString"),
                warningMsg: t("exceedingAvailableStockString"),
                isThresholdWarning: multiPackCurrentPieces > MAXIMUM_QUANTITY,
              },
            }
          : {
              settings: {
                buttonLabel: t("multipackSettingsTitleString"),
                title: t("multipackSettingsTitleString"),
                label: t("howManyPiecesMultiPackString"),
                suffixLabel: t("piecesMultiPackString"),
                initialValue: typedAmountSettings?.pieces_per_multi_pack ?? "",
                disabled: isRefillQuantityDisabled,
                onConfirm: (value: string) =>
                  saveAmountSetting("pieces_per_multi_pack", value),
              },
            }),
      },
      [RefillUnitType.PALLET]: {
        label: t("palletString"),
        ...(hasPalletSetting
          ? {
              panelProps: {
                type: RefillUnitType.PALLET,
                value: getDisplayedUnitValue(RefillUnitType.PALLET),
                onChange: updateRefillQuantity(RefillUnitType.PALLET),
                infoText: t("palletUnit_infoText", {
                  defaultValue: `1 ${t("palletUnit")} = ${piecesPerPallet} ${t("piecesString").toLowerCase()}`,
                }),
                suffixLabel: t("palletsString"),
                max: maxPieces,
                current: palletCurrentPieces,
                disabled: isRefillQuantityDisabled,
                isError: palletCurrentPieces > maxPieces,
                isWarning: palletCurrentPieces > availablePieces,
                errorMsg: t("exceedingMaxPiecesString"),
                warningMsg: t("exceedingAvailableStockString"),
                isThresholdWarning: palletCurrentPieces > MAXIMUM_QUANTITY,
              },
            }
          : {
              settings: {
                buttonLabel: t("palletSettingsTitleString"),
                title: t("palletSettingsTitleString"),
                label: t("howManyPiecesPalletString"),
                suffixLabel: t("piecesPalletString"),
                initialValue: typedAmountSettings?.pieces_per_pallet ?? "",
                disabled: isRefillQuantityDisabled,
                onConfirm: (value: string) =>
                  saveAmountSetting("pieces_per_pallet", value),
              },
            }),
      },
    }),
    [
      t,
      getDisplayedUnitValue,
      updateRefillQuantity,
      maxPieces,
      piecesCurrentPieces,
      isRefillQuantityDisabled,
      availablePieces,
      hasMultiPackSetting,
      multiPackCurrentPieces,
      piecesPerMultiPack,
      typedAmountSettings?.pieces_per_multi_pack,
      saveAmountSetting,
      hasPalletSetting,
      palletCurrentPieces,
      piecesPerPallet,
      typedAmountSettings?.pieces_per_pallet,
    ]
  );

  const isExceedingMaxPieces = currentPieces > maxPieces;
  const valid = currentPieces > 0 && maxPieces >= 0 && !isExceedingMaxPieces;

  return {
    activeUnit,
    currentPieces,
    handleRefillUnitTypeChange,
    panelConfigs,
    valid,
    isRefillQuantityDisabled,
  };
};
